'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { displayTierName } from '@/util/product_client_safe';
import cn from 'classnames';
import Image from 'next/image';
import NextLink from 'next/link';
import dynamic from 'next/dynamic';
import { useRouter, useSearchParams } from 'next/navigation';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

import { useLoggedInUser } from '@/components/hook/user';
import { useModal } from '@/components/hook/modal';
import LazyRelatedProducts from './LazyRelatedProducts';
import { useWishlist } from '@/components/hook/wishlist';
import {
  useCloset,
  useClosetValidationForProductTier
} from '@/components/hook/closet';

import { useCart } from '@/components/hook/cart';
import LoginModal from '@/components/modal/LoginModal/LoginModal';
import { LoginFormValues } from '@/components/form/LoginForm/LoginForm';
import SimpleModal from '@/components/modal/SimpleModal/SimpleModal';
import SizeSelectModal from '@/components/modal/SizeSelectModal/SizeSelectModal';
import Text from '@/components/common/Text/Text';
import { Tooltip } from 'react-tooltip';
import { useRef } from 'react';
import Link from '@/components/common/Link/Link';
import SlickCarousel from '@/components/common/SlickCarousel/SlickCarousel';
import AnimatedAccordion from '@/components/common/AnimatedAccordion/AnimatedAccordion';
import RichText from '@/components/common/RichText/RichText';
import Breadcrumbs from '@/components/common/Breadcrumbs/Breadcrumbs';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import ProductCardCarousel from '@/components/common/ProductCardCarousel/ProductCardCarousel';
import ProductCard from '@/components/common/ProductCard/ProductCard';
import ProductZoomModal from '@/components/modal/ProductZoomModal/ProductZoomModal';
import ProductZoomImage from '@/components/common//ProductZoomImage/ProductZoomImage';

// Loaded dynamically so it is excluded from this page's static chunk — adding
// useCookieConsent directly here would shift module IDs and trigger the Next.js
// 13 RSC chunk double-encoding bug. See docs/gotchas.md.
const TangibleeInit = dynamic(
  () => import('@/components/common/TangibleeInit/TangibleeInit'),
  { ssr: false }
);

import { CLOSET_SLOT_TIER_SLUG, ClosetValidationStatus } from '@/types/Closet';

import HeartOutlined from 'public/img/icon/heart-outline-black.svg';
import HeartFilled from 'public/img/icon/heart-filled-black.svg';
import FallbackImage from 'public/img/fallback-image.jpg';

import {
  ProductDetailData,
  ProductCardData,
  ProductSizeVariant
} from '@/types/Product';
import { User } from '@/types/User';

import AddToClosetSection from './AddToClosetSection';
import { getSwapDateForTierInCloset } from './dataTools';
import {
  trackAddToClosetClick,
  trackModalCtaClick,
  trackAccordionAction,
  trackProductViewed
} from './productDetailAnalytics';

import analytics from '@/util/analytics';
import { capitalize } from '@/util/misc';
import type {
  PriveeAddToClosetVariant,
  PriveeAccordionSection
} from '@/util/privee_analytics';
import { datadogLogs } from '@datadog/browser-logs';

import './ProductDetailApp.style.scss';
import { useFlags } from '@/components/hook/flags';
import { FLAG_KEYS } from '@/const/flags';
import CircleQuestionMark from 'public/img/icon/circle-questionmark.svg';
import { customerHasPremierAddOn, getCustomerTier } from '@/util/customer';
import { useSearch } from '@/components/hook/search';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';

const CompleteTheLookRecommendationGrid = dynamic(
  () =>
    import(
      '@/components/builder/CompleteTheLookRecommendationGrid/CompleteTheLookRecommendationGrid'
    ),
  { ssr: false }
);

type ProductDetailAppProps = {
  className?: string;
  productData: ProductDetailData;
  productCardData: ProductCardData;
  relatedProducts?: ProductCardData[];
  prices: { [key: string]: string };
  hideWishlist?: boolean;
  hideBreadcrumbs?: boolean;
  hideCompleteTheLook?: boolean;
  hideSubText?: boolean;
  isPriveeProduct?: boolean;
};

type PDPAccordionState = {
  description: boolean;
  size: boolean;
  condition: boolean;
};

const ALL_ACCORDIONS_CLOSED: PDPAccordionState = {
  description: false,
  size: false,
  condition: false
};

/**
 * Below this viewport width, Suggested Items ("You'll Also Love These Classics")
 * renders above Complete The Look, for a marketing click-through test on
 * merchandising placement (VIVE-3513).
 */
const MWEB_ORDER_SWAP_MAX_WIDTH = 768;

function ProductDetailApp(props: ProductDetailAppProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const node = bottomRef.current;
    if (!node) return;
    const observer = new window.IntersectionObserver(
      () => {
        setIsSticky(true);
      },
      {
        root: null, // viewport
        threshold: 0,
        rootMargin: '0px 0px 100px 0px'
      }
    );
    observer.observe(node);
    return () => {
      observer.unobserve(node);
    };
  }, []);

  const completeTheLookTooltipRef = useRef<any>(null);
  const {
    className = '',
    productData,
    productCardData,
    relatedProducts,
    prices,
    hideWishlist,
    hideBreadcrumbs,
    hideCompleteTheLook,
    hideSubText,
    isPriveeProduct = false
  } = props;

  useEffect(() => {
    trackProductViewed(productData);
  }, [productData.sku, productData.productTier]);

  const childQuery = useMemo(
    () => ({
      searchQuery: productData.sku,
      showOnlyChildProducts: true
    }),
    [productData.sku]
  );

  const { loading: childProductsSearchLoading, results: childProducts } =
    useSearch(childQuery);

  const modalTools = useModal();
  const { value: closetValue } = useCloset();
  const { value: cartValue, actions: cartActions } = useCart();
  const { actions: wishlistActions, value: wishlistValue } = useWishlist();
  const router = useRouter();
  const searchParams = useSearchParams();

  const { value: flagValues } = useFlags();
  const isCompleteTheLookOn = flagValues[FLAG_KEYS.ELLA_COMPLETE_THE_LOOK];

  const { isIOSWebView } = useContextBroker();

  // Tracked live rather than only at mount, because the acceptance criteria are
  // a viewport condition: every phone rotation crosses this breakpoint (portrait
  // ~320-430px, landscape ~568-932px), and a mount-only check would leave a
  // rotated device in the wrong order until reload.
  //
  // Starts false so the first client render matches SSR — both modules are below
  // the fold and populated client-side, so correcting it in the effect is not
  // visible. React bails out on an unchanged boolean, so only crossing the
  // breakpoint re-renders; ordinary resizes within a range are free. Crossing it
  // does move the section between slots, which remounts the grid and repeats its
  // /api/ella/recommendations call.
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);

  useEffect(() => {
    const evaluateViewport = () =>
      setIsNarrowScreen(window.innerWidth < MWEB_ORDER_SWAP_MAX_WIDTH);

    evaluateViewport();
    window.addEventListener('resize', evaluateViewport);
    return () => window.removeEventListener('resize', evaluateViewport);
  }, []);

  // The iOS app webview is narrower than the swap breakpoint, so it needs an
  // explicit exclusion to keep the in-app order unchanged.
  const showSuggestedFirst = isNarrowScreen && !isIOSWebView;

  useEffect(() => {
    const firstDesktopProductImages = document.querySelectorAll(
      '.ProductZoomImage-img'
    )[0];
    const desktopImageContainer = document.querySelector(
      '.images-desktop-container'
    ) as Element;
    const isImageContainerVisible =
      window.getComputedStyle(desktopImageContainer).display === 'flex';

    if (
      isImageContainerVisible &&
      window.scrollY === 0 &&
      window.innerHeight < 880
    ) {
      // On desktop with a short window (macbook 13), we want to scroll
      // the page up so the product is in view on page load
      firstDesktopProductImages?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }, []);

  const {
    loading: closetValidationLoading,
    closetValidationStatus,
    closetValidationSwapDate,
    revalidate
  } = useClosetValidationForProductTier(
    productData.id,
    productData.productTier.toLowerCase() as CLOSET_SLOT_TIER_SLUG,
    cartValue
  );

  // TODO: Convert to Reducer
  const [accordionState, setAccordionState] = useState<PDPAccordionState>(
    ALL_ACCORDIONS_CLOSED
  );

  const [isWishlistOpLoading, setIsWishlistOpLoading] =
    useState<boolean>(false);
  const [isWishListingFromLogin, setIsWishListingFromLogin] =
    useState<boolean>(false);
  const [removeItemFromWishlist, setRemoveItemFromWishlist] = useState<boolean>(
    searchParams.get('act') === 'remove-item' || false
  );

  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);

  const user = useLoggedInUser();

  /**
   * Only used after a login event. Refreshes the user's cart validation status
   */
  const refreshClosetValidationStatus = async (
    _: LoginFormValues,
    newUser: User
  ): Promise<void> => {
    revalidate();
  };

  const handleAddToCart = useCallback(async () => {
    const clickId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    datadogLogs.logger.info('add_to_cart_click_start', {
      category: 'add_to_cart_diagnostic',
      layer: 'ProductDetailApp',
      clickId,
      productId: productData.id,
      productSku: productData.sku,
      productTier: productData.productTier,
      isSized: !!productData.sizeVariants,
      sizeVariantCount: productData.sizeVariants?.length ?? 0,
      sizeVariants: productData.sizeVariants ?? null,
      closetValidationStatus,
      userId: user?.id ?? null
    });

    // HANDLE SHOWING MODAL.
    // If this switch statement does not fire, then add the item to the user's cart
    const swapData = getSwapDateForTierInCloset(
      productData.productTier.toLowerCase() as CLOSET_SLOT_TIER_SLUG,
      closetValue
    );

    const modalSwapDate =
      swapData?.canSwapDate ||
      (closetValidationSwapDate ? new Date(closetValidationSwapDate) : null);

    let addToClosetAnalyticState: PriveeAddToClosetVariant = 'Clicked';
    let modalTitle, modalCopy, modalCta;

    // Set modal copy depending on status
    switch (closetValidationStatus) {
      case ClosetValidationStatus.TooEarly: {
        addToClosetAnalyticState = 'Too Early';
        modalTitle = `You're too early`;
        modalCta = 'Go to My Account';
        const isPriveeTier = productData.productTier.toLowerCase() === 'privee';
        modalCopy = isPriveeTier
          ? `View your account to see when you're eligible for a new item or contact your concierge to source a similar item.`
          : `View your account to see when you're eligible for a new item or add it to your wishlist to save it for another month!`;
        if (swapData?.remainingDays) {
          modalCopy = `You can swap in ${swapData.remainingDays} days. ${modalCopy}`;
        }
        break;
      }

      case ClosetValidationStatus.TooEarlyOrUpgrade: {
        addToClosetAnalyticState = 'Too Early or Upgrade';
        modalTitle = 'Upgrade your membership';
        modalCta = 'Upgrade your membership';

        if (modalSwapDate) {
          modalCopy = `Upgrade your membership or wait until ${format(
            modalSwapDate,
            'LLLL d'
          )} to borrow this item`;
        } else {
          modalCopy = `Upgrade your membership to borrow this item`;
        }
        break;
      }

      case ClosetValidationStatus.UpgradeOrReturn: {
        modalTitle = 'Upgrade your membership or return';
        modalCta = 'return an item'; // lowercase because it's appended to a sentence
        modalCopy =
          'Upgrade your membership or return an item in your closet to borrow this item.';
        break;
      }

      case ClosetValidationStatus.Return: {
        addToClosetAnalyticState = 'Upgrade or Return';
        modalTitle = 'Return an item to open up your closet slot';
        modalCta = 'Return your item';
        modalCopy =
          'You currently have an item at home. In order to borrow a new item, please click return on your current item.';
        break;
      }
    }

    // If modal copy exists, show modal and short circuit
    if (modalTitle && modalCopy && modalCta) {
      modalTools.showModal(
        <SimpleModal
          title={modalTitle}
          onClose={modalTools.closeModal}
          className='ProductDetailApp-modal'
        >
          <Text>{modalCopy}</Text>
          <Text
            variant='text-s'
            className='ProductDetailApp-modal-cta'
          >
            {closetValidationStatus ===
            ClosetValidationStatus.UpgradeOrReturn ? (
              <>
                <Link
                  href='/account/upgrade'
                  onClick={() => {
                    trackModalCtaClick(
                      'Upgrade Your Membership',
                      isPriveeProduct,
                      {
                        parent_sku: productData.sku,
                        parent_sku_tier: productData.productTier
                      }
                    );
                  }}
                  underline
                  compact
                >
                  Upgrade your membership
                </Link>{' '}
                or{' '}
                <Link
                  href='/account'
                  onClick={() => {
                    trackModalCtaClick('Go to My Account', isPriveeProduct, {
                      parent_sku: productData.sku,
                      parent_sku_tier: productData.productTier
                    });
                  }}
                  underline
                  compact
                >
                  {modalCta}
                </Link>
              </>
            ) : closetValidationStatus ===
              ClosetValidationStatus.TooEarlyOrUpgrade ? (
              <Link
                href='/account/upgrade'
                onClick={() => {
                  trackModalCtaClick(
                    'Upgrade Your Membership',
                    isPriveeProduct,
                    {
                      parent_sku: productData.sku,
                      parent_sku_tier: productData.productTier
                    }
                  );
                }}
                underline
                compact
              >
                Upgrade your membership
              </Link>
            ) : (
              <Link
                href='/account'
                onClick={() => {
                  trackModalCtaClick(
                    closetValidationStatus === ClosetValidationStatus.Return
                      ? 'Return an Item'
                      : 'Go to My Account',
                    isPriveeProduct,
                    {
                      parent_sku: productData.sku,
                      parent_sku_tier: productData.productTier
                    }
                  );
                }}
                underline
                compact
              >
                {modalCta}
              </Link>
            )}
          </Text>
        </SimpleModal>
      );

      return;
    }

    if (productData.sizeVariants) {
      const sizeVariants: (ProductSizeVariant & { isInStock: boolean })[] =
        productData.sizeVariants.map((variant) => ({
          ...variant,
          isInStock:
            childProducts?.data.hits.find(
              (c) => c.objectID === variant.addToCartUUID
            )?.stock_boolean ?? false
        }));

      modalTools.showModal(
        <SizeSelectModal
          clickId={clickId}
          onClose={modalTools.closeModal}
          sizeVariants={sizeVariants}
          onSizeConfirm={async (selectedSize) => {
            if (!selectedSize) {
              datadogLogs.logger.warn('add_to_cart_no_size_selected', {
                category: 'add_to_cart_diagnostic',
                layer: 'ProductDetailApp',
                clickId,
                productId: productData.id,
                productSku: productData.sku
              });
              return;
            }
            datadogLogs.logger.info('add_to_cart_sized_dispatch', {
              category: 'add_to_cart_diagnostic',
              layer: 'ProductDetailApp',
              clickId,
              productId: productData.id,
              productSku: productData.sku,
              productTier: productData.productTier,
              selectedSizeUUID: selectedSize.addToCartUUID,
              selectedSizeDescription: selectedSize.sizeDescription
            });
            // We don't need to set a loading state here
            // Because the modal handles this internally
            // setIsAddingToCart(true);
            try {
              await cartActions.addToCart(selectedSize.addToCartUUID, clickId);
              datadogLogs.logger.info('add_to_cart_sized_success', {
                category: 'add_to_cart_diagnostic',
                layer: 'ProductDetailApp',
                clickId,
                productId: productData.id,
                productSku: productData.sku,
                selectedSizeUUID: selectedSize.addToCartUUID
              });
            } catch (error) {
              datadogLogs.logger.error('add_to_cart_sized_failed', {
                category: 'add_to_cart_diagnostic',
                layer: 'ProductDetailApp',
                clickId,
                productId: productData.id,
                productSku: productData.sku,
                productTier: productData.productTier,
                selectedSizeUUID: selectedSize.addToCartUUID,
                error: error instanceof Error ? error.message : String(error)
              });
              throw error;
            }
            modalTools.closeModal();
            router.push('/cart');
          }}
        />
      );
      // Early return here so all add to cart functions is handled
      // via the modal
      return;
    }

    try {
      setIsAddingToCart(true);
      datadogLogs.logger.info('add_to_cart_unsized_dispatch', {
        category: 'add_to_cart_diagnostic',
        layer: 'ProductDetailApp',
        clickId,
        productId: productData.id,
        productSku: productData.sku,
        productTier: productData.productTier
      });
      await cartActions.addToCart(productData.id, clickId);
      datadogLogs.logger.info('add_to_cart_unsized_success', {
        category: 'add_to_cart_diagnostic',
        layer: 'ProductDetailApp',
        clickId,
        productId: productData.id,
        productSku: productData.sku
      });
      router.push('/cart');

      // This forces the above route to do a hard refresh;
      // i.e. generate fresh recommendations for the item(s) in cart
      router.refresh();

      // Comment out for now, it prematurely returns CTA to active.
      // On PDP reload it should reset.
      // setIsAddingToCart(false);
    } catch (error) {
      setIsAddingToCart(false);
      datadogLogs.logger.error('add_to_cart_unsized_failed', {
        category: 'add_to_cart_diagnostic',
        layer: 'ProductDetailApp',
        clickId,
        productId: productData.id,
        productSku: productData.sku,
        productTier: productData.productTier,
        error: error instanceof Error ? error.message : String(error)
      });
      console.error(`[ERROR] - handleAddToCart - ${error}`);
    }

    trackAddToClosetClick(addToClosetAnalyticState, isPriveeProduct, {
      parent_sku: productData.sku,
      parent_sku_tier: productData.productTier
    });
  }, [
    productData.id,
    productData.sizeVariants,
    productData.productTier,
    cartActions,
    closetValidationStatus,
    modalTools,
    closetValue,
    router
  ]);

  const {
    productName,
    brand,
    productTier,
    estimatedRetailValue,
    descriptionHTML,
    sizeHTML,
    conditionHTML,
    breadcrumbs
  } = productData;

  const formattedRetailPriceWithNoCents = estimatedRetailValue.split('.')[0];

  let images =
    productData.images.length === 0 ? [FallbackImage] : productData.images;

  const handleAccordionAction = (accordionName: keyof PDPAccordionState) => {
    const isCurrentlyOpenedAccordion = accordionState[accordionName] === true;
    const OpenedAccordion = Object.keys(accordionState).filter(function (key) {
      return accordionState[key as keyof PDPAccordionState];
    });

    if (OpenedAccordion[0]) {
      trackAccordionAction(
        capitalize(OpenedAccordion[0].toString()) as PriveeAccordionSection,
        'Collapsed',
        isPriveeProduct,
        {
          parent_sku: productData.sku,
          parent_sku_tier: productData.productTier
        }
      );
    }

    if (isCurrentlyOpenedAccordion) {
      setAccordionState({
        ...ALL_ACCORDIONS_CLOSED
      });

      return;
    }

    setAccordionState({
      ...ALL_ACCORDIONS_CLOSED,
      [accordionName]: true
    });

    trackAccordionAction(
      capitalize(accordionName) as PriveeAccordionSection,
      'Expanded',
      isPriveeProduct,
      {
        parent_sku: productData.sku,
        parent_sku_tier: productData.productTier
      }
    );
  };

  const handleWishlisting = async () => {
    setIsWishlistOpLoading(true);
    const isCurrentlyWishlisted = wishlistValue.wishlistAsMap[productData.sku];
    let analyticWishlistAction = 'Add to';

    if (isWishListingFromLogin && isCurrentlyWishlisted) {
      setIsWishlistOpLoading(false);
      return;
    }

    // ADD to wishlist
    if (isCurrentlyWishlisted || removeItemFromWishlist) {
      const wishlistedItem = wishlistValue.wishlist.find((item) => {
        return item.sku === productData.sku;
      });

      analyticWishlistAction = 'Remove from';

      if (wishlistedItem) {
        await wishlistActions.removeFromWishlist(wishlistedItem.wishlistItemId);
      }

      setIsWishlistOpLoading(false);
      toast('Item removed from wishlist.');
    } else {
      setIsWishlistOpLoading(true);
      await wishlistActions.addToWishlist(productData.sku);
      setIsWishlistOpLoading(false);
    }

    analytics.track(`${analyticWishlistAction} Wishlist`, {
      parent_sku: productData.sku,
      parent_sku_tier: productData.productTier
    });
  };

  const handleWishlistClick = async () => {
    if (!user) {
      modalTools.showModal(
        <LoginModal
          headline='Login to add to wishlist'
          initialValues={{ username: '', password: '' }}
          onClose={modalTools.closeModal}
          onSubmitSuccess={(_: LoginFormValues, resp: unknown) => {
            // Refresh closet validation status
            refreshClosetValidationStatus(_, resp as User);
            setIsWishListingFromLogin(true);
            modalTools.closeModal();
          }}
          onNavigateClick={modalTools.closeModal}
        />,
        { topClassName: 'bg-gold' }
      );
      return;
    }

    handleWishlisting();
  };

  useEffect(() => {
    if (isWishListingFromLogin) {
      handleWishlisting();
      setIsWishListingFromLogin(false);
    }

    if (removeItemFromWishlist) {
      handleWishlisting();
      setRemoveItemFromWishlist(false);
    }
  }, [isWishListingFromLogin, removeItemFromWishlist]);

  const analyticsTooltip = () => {
    analytics.track('PDP CTL Tooltip - Clicked', {
      parent_sku: productData.sku,
      parent_sku_tier: productData.productTier,
      membership_tier: getCustomerTier(
        closetValue.closet?.subscriptionPlanProducts || []
      ),
      add_ons: customerHasPremierAddOn(
        closetValue.closet?.subscriptionPlanProducts || []
      )
    });
  };

  // Declared once and rendered into exactly one of two slots below, so the
  // recommendation grid never double-mounts and the tooltip id stays unique.
  const completeTheLookSection =
    isCompleteTheLookOn && !hideCompleteTheLook ? (
      <div className='pdp-complete-the-look'>
        <div className='complete-the-look-title'>
          <Text
            variant='title-xl'
            italic
          >
            Complete The Look
          </Text>
          <button
            className='info-tooltip-trigger'
            data-tooltip-id='complete-the-look-tooltip'
          >
            <Image
              className=''
              src={CircleQuestionMark}
              alt=''
            />
          </button>
          <Tooltip
            ref={completeTheLookTooltipRef}
            id='complete-the-look-tooltip'
            className='complete-the-look-tooltip'
            place='top'
            clickable
            openOnClick={true}
            afterShow={analyticsTooltip}
          >
            <div className='tooltip-content'>
              <button
                className='tooltip-close-button'
                onClick={() => {
                  completeTheLookTooltipRef.current?.close();
                }}
              >
                ✕
              </button>
              <div>
                <Text
                  variant='title-xxs-500'
                  uppercase
                >
                  What is Complete The Look?
                </Text>

                <Text
                  variant='span-s'
                  className='lh-150'
                >
                  See below for personalized outfit inspiration from REVOLVE and
                  FWRD to match your Vivrelle accessories. Click on the items to
                  learn more and shop.
                </Text>
              </div>
              <div>
                <Text
                  variant='title-xxs-500'
                  uppercase
                >
                  How do I earn 5% of my purchase value as vivrelle credits?
                </Text>

                <Text
                  variant='span-s'
                  className='lh-150'
                >
                  To redeem your 5% Vivrelle credit after purchase, either
                  forward your order confirmation email to{' '}
                  <Link
                    href='mailto:membership@vivrelle.com'
                    compact
                    newTab
                    underline
                    variant={'link-s'}
                  >
                    membership@vivrelle.com
                  </Link>{' '}
                  OR submit your receipt through our{' '}
                  <Link
                    href='https://yutflbmz3e1.typeform.com/completethelook'
                    compact
                    newTab
                    underline
                    variant={'link-s'}
                  >
                    online form
                  </Link>
                  . Credits will be added to your account within 30 days. Please
                  ensure the name on your order matches your Vivrelle account
                  name. Any returns will affect your credit amount.
                </Text>
              </div>
            </div>
          </Tooltip>
        </div>
        <CompleteTheLookRecommendationGrid
          numItemsToShow={4}
          product={productCardData}
        />
      </div>
    ) : null;

  return (
    <div
      className={cn('ProductDetailApp', className, {
        'suggested-first': showSuggestedFirst
      })}
    >
      {/* Tangiblee: loaded via dynamic import to keep it out of this page's
          static chunk and gate it behind functional consent. See TangibleeInit. */}
      <TangibleeInit />
      <div className='shell-contained'>
        <div className='pdp-page-container'>
          <div className='image-container'>
            <SlickCarousel
              className='mobile-carousel'
              slickSettings={{
                infinite: true,
                dots: true,
                arrows: false,
                centerMode: true,
                afterChange: () => {
                  const imageFile = document.querySelector(
                    '.slick-active .pdp-mobile-carousel-img-container'
                  ) as HTMLElement;
                  const imageFileName = /(?<=\/)[^\/\?#]+(?=[^\/]*$)/.exec(
                    imageFile.dataset.imageUrl as string
                  )![0];

                  if (imageFile) {
                    analytics.track('PDP Image - Viewed mobile', {
                      image_file_name: imageFileName,
                      parent_sku: productData.sku
                    });
                  }
                }
              }}
            >
              {images.map((imageUrl, index) => {
                return (
                  <div
                    className='pdp-mobile-carousel-img-container'
                    data-image-url={imageUrl}
                    key={index}
                    onClick={() => {
                      modalTools.showModal(
                        <ProductZoomModal
                          imageSrc={imageUrl as string}
                          onClose={modalTools.closeModal}
                        />,
                        {
                          isFullScreen: true
                        }
                      );
                    }}
                  >
                    <Image
                      className='pdp-mobile-carousel-img'
                      src={imageUrl as string}
                      alt={`${productName}`}
                      width={0}
                      height={0}
                    />
                  </div>
                );
              })}
            </SlickCarousel>

            <div className='images-desktop-container'>
              {images.map((imageUrl, index) => {
                return (
                  <ProductZoomImage
                    altText={`${productName}`}
                    key={index}
                    imageSrc={imageUrl as string}
                  />
                );
              })}
            </div>

            {!hideBreadcrumbs && <Breadcrumbs data={breadcrumbs} />}
          </div>
          <div className={cn('product-container', { sticky: isSticky })}>
            <AddToClosetSection
              className='add-to-closet-mobile'
              closetValidationStatus={closetValidationStatus}
              onButtonClick={handleAddToCart}
              onLoginSuccess={refreshClosetValidationStatus}
              loading={
                isAddingToCart ||
                closetValidationLoading ||
                childProductsSearchLoading
              }
              disabled={
                isAddingToCart ||
                closetValidationLoading ||
                childProductsSearchLoading
              }
              productData={productData}
              hideSubText={hideSubText}
              isPriveeProduct={isPriveeProduct}
            />
            <div className='product-details'>
              <div className='product-details-baseInfo'>
                <div className='product-details-baseInfo-left'>
                  <Text
                    variant='title-xs'
                    as='h1'
                  >
                    <NextLink
                      href={`/collection?brand=${encodeURIComponent(brand)}`}
                    >
                      {brand}
                    </NextLink>
                  </Text>
                  <Text
                    className='product-name'
                    variant='text-s'
                    as='h2'
                    data-testid='pdp-product-name'
                  >
                    {productName}
                  </Text>
                  <Text
                    className='product-subscription-level'
                    variant='text-s'
                    as='h3'
                  >
                    {displayTierName(productTier)} Item
                    {user === undefined && ` (${prices[productTier]}/month)`}
                  </Text>
                  <Text
                    className='product-retail-value'
                    variant='text-s'
                  >
                    {formattedRetailPriceWithNoCents}{' '}
                    {productTier.toLowerCase() === 'privee'
                      ? 'estimated resale value'
                      : 'estimated retail'}
                  </Text>
                </div>
                {!hideWishlist && (
                  <div className='product-details-baseInfo-right'>
                    {isWishlistOpLoading ? (
                      <LoadingSpinner size={16} />
                    ) : (
                      <Image
                        className='cursor'
                        src={
                          wishlistValue.wishlistAsMap[productData.sku]
                            ? HeartFilled
                            : HeartOutlined
                        }
                        height={16}
                        width={16}
                        alt='Wishlist this item'
                        onClick={handleWishlistClick}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className='product-details-extendedInfo'>
                <AddToClosetSection
                  className='add-to-closet-desktop'
                  closetValidationStatus={closetValidationStatus}
                  onButtonClick={handleAddToCart}
                  onLoginSuccess={refreshClosetValidationStatus}
                  loading={isAddingToCart || closetValidationLoading}
                  disabled={isAddingToCart || closetValidationLoading}
                  productData={productData}
                  hideSubText={hideSubText}
                  isPriveeProduct={isPriveeProduct}
                />
                <div className='accordion-container'>
                  <AnimatedAccordion
                    className='accordion-description'
                    open={accordionState.description}
                    title='Description'
                    expandedHeight={250}
                    onRequestChange={() => {
                      handleAccordionAction('description');
                    }}
                  >
                    <RichText className='pdp-description-html'>
                      {descriptionHTML}
                    </RichText>
                  </AnimatedAccordion>
                  <AnimatedAccordion
                    className='accordion-size'
                    title='Size'
                    open={accordionState.size}
                    expandedHeight={70}
                    onRequestChange={() => {
                      handleAccordionAction('size');
                    }}
                  >
                    <RichText className='pdp-description-html'>
                      {sizeHTML}
                    </RichText>
                  </AnimatedAccordion>

                  <AnimatedAccordion
                    className='accordion-condition'
                    title='Condition'
                    open={accordionState.condition}
                    expandedHeight={140}
                    onRequestChange={() => {
                      handleAccordionAction('condition');
                    }}
                  >
                    <RichText className='pdp-description-html'>
                      {conditionHTML}
                    </RichText>
                  </AnimatedAccordion>
                </div>
                {brand.toLowerCase() !== 'vivrelle' && (
                  <Text
                    className='product-brand-affiliate'
                    variant='text-s'
                  >
                    {brand} is a registered trademark of {brand}. <br />
                    Vivrelle is not affiliated with {brand}.
                  </Text>
                )}
              </div>
              {!showSuggestedFirst && completeTheLookSection}
            </div>
            <div ref={bottomRef} />
          </div>
        </div>
      </div>
      <LazyRelatedProducts
        brand={productData.brand}
        subscriptionLvl={productData.productTier}
        currentProductId={productData.id}
      />
      {showSuggestedFirst && completeTheLookSection && (
        // Re-supplies the horizontal padding the section inherits from
        // .product-details in its default slot.
        <div className='pdp-complete-the-look-mobile-slot'>
          {completeTheLookSection}
        </div>
      )}
    </div>
  );
}

export default ProductDetailApp;
